<script setup lang="ts">
import { computed } from "vue";
import type { ICaseRequest } from "~~/types/case-request";

const props = defineProps<{
  open: boolean;
  request: ICaseRequest | null;
}>();

const emit = defineEmits<{
  viewRequest: [id: string];
  dismiss: [];
}>();

const router = useRouter();

const handleViewRequest = () => {
  if (props.request) {
    emit("viewRequest", props.request.id);
    router.push(`/case-request/${props.request.id}`);
  }
};

const handleDismiss = () => {
  emit("dismiss");
};

const customerName = computed(() => props.request?.customer?.name || "Unknown");
const vehicleInfo = computed(() => {
  const vehicle = props.request?.customer?.vehicle;
  if (!vehicle) return "No vehicle info";
  return `${vehicle.brand || ""} ${vehicle.model || ""} (${vehicle.regNum || "N/A"})`.trim();
});
const location = computed(() => {
  const from = props.request?.location?.from;
  if (!from) return "Unknown location";
  return from.address || from.generatedAddress || "No address available";
});
</script>

<template>
  <Dialog :open="open">
    <DialogContent hide-close-button class="sm:max-w-md">
      <DialogHeader>
        <DialogTitle class="text-xl">New Service Request</DialogTitle>
        <DialogDescription> A new request has been created and requires attention. </DialogDescription>
      </DialogHeader>

      <div v-if="request" class="py-4 space-y-3">
        <div class="space-y-1">
          <p class="text-sm font-medium text-muted-foreground">Request ID</p>
          <p class="text-sm font-semibold">{{ request.id }}</p>
        </div>

        <div class="space-y-1">
          <p class="text-sm font-medium text-muted-foreground">Customer</p>
          <p class="text-sm">{{ customerName }}</p>
        </div>

        <div class="space-y-1">
          <p class="text-sm font-medium text-muted-foreground">Vehicle</p>
          <p class="text-sm">{{ vehicleInfo }}</p>
        </div>

        <div class="space-y-1">
          <p class="text-sm font-medium text-muted-foreground">Location</p>
          <p class="text-sm">{{ location }}</p>
        </div>
      </div>

      <DialogFooter class="gap-2">
        <Button variant="outline" @click="handleDismiss"> Dismiss </Button>
        <Button variant="default" @click="handleViewRequest"> View Request </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
